import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Cpu,
    Search,
    BarChart3,
    Newspaper,
    Activity,
    Bot,
    User,
    Sparkles,
    ArrowUpRight,
    Pause,
    Play,
    RotateCcw,
} from 'lucide-react';

import MarketIndexCard from '../../features/kquant/components/MarketIndexCard';
import MarketHeatmapCard from '../../features/kquant/components/MarketHeatmapCard';
import { MarkdownRenderer } from '../../features/kquant/components/MarkdownRenderer';
import { SectorNewsPanel } from '../KQuantSmartInsightsPage/HotTopicsSection.SectorNewsPanel';
import { chartData } from './mockData';
import type { HeatmapNode } from '../../features/kquant/types';
import type { SectorPanelData } from '../KQuantSmartInsightsPage/HotTopicsSection.types';
import { heatmapData, newsData } from './mockData';
import { ChatBar } from '../../features/kquant/components/ChatBar';
import { FINAL_RESPONSE_TEXT } from './mockData';
import VideoCarousel from './VideoCarousel';
import type { VideoSlide } from './VideoCarousel';
const TALK_SLIDES: VideoSlide[] = [
    {
        src: '/assets/home/quant1.mp4',
        label: 'Macro Market Analysis',
        caption: 'Investors obtain important daily financial news from news websites.',
    },
    {
        src: '/assets/home/quant2.mp4',
        label: 'Price–Volume Factor Analysis',
        caption: 'Investors obtain real-time prices and market trends through brokerage platforms.',
    },
    {
        src: '/assets/home/quant3.mp4',
        label: 'Price Factor Visualization',
        caption: 'Market conditions are visualized and analyzed through code based on the collected data.',
    },
];

const MOCK_COLLECTIONS = [
    { id: 'nasdaq', label: 'Nasdaq 100', asset_class: 'equity', kind: 'index' },
];

const HEATMAP_DATA: HeatmapNode = heatmapData;
const NEWS_PANEL_DATA: SectorPanelData = newsData;

// Step durations in ms for progress bar fill animation
// Steps: 0=user-msg, 1=analyzing, 2=tool-call, 3=ai-response
const STEP_DURATIONS = [400, 700, 500, 2500];

// Stages at which each step completes
const STEP_STAGE_END = [1, 2, 3, 4];

function StepProgressBar({
    currentStep,
    stepProgress,
    isPaused,
    onStepClick,
    onPause,
    onReplay,
}: {
    currentStep: number;
    stepProgress: number;
    isPaused: boolean;
    onStepClick: (step: number) => void;
    onPause: () => void;
    onReplay: () => void;
}) {
    const steps = ['User', 'Analyzing', 'Tool Call', 'Response'];
    return (
        <div className="flex flex-col items-center gap-3">
            {/* Dots / pills */}
            <div className="flex items-center gap-2.5">
                {steps.map((label, i) => {
                    const isCompleted = i < currentStep;
                    const isActive = i === currentStep;
                    return (
                        <button
                            key={label}
                            onClick={() => onStepClick(i)}
                            title={label}
                            className="relative flex items-center cursor-pointer"
                            style={{ outline: 'none' }}
                        >
                            {isActive ? (
                                <div
                                    className="relative overflow-hidden rounded-full bg-slate-200"
                                    style={{ width: 48, height: 8 }}
                                >
                                    <div
                                        className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
                                        style={{
                                            width: `${Math.round(stepProgress * 100)}%`,
                                            transition: 'width 80ms linear',
                                        }}
                                    />
                                </div>
                            ) : (
                                <div
                                    className={`rounded-full transition-all duration-300 ${
                                        isCompleted
                                            ? 'bg-blue-400 border border-blue-400'
                                            : 'bg-slate-200 border border-slate-300'
                                    }`}
                                    style={{ width: 8, height: 8 }}
                                />
                            )}
                        </button>
                    );
                })}
            </div>
            {/* Controls */}
            <div className="flex items-center gap-2">
                <button
                    onClick={onPause}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                    title={isPaused ? 'Resume' : 'Pause'}
                >
                    {isPaused ? <Play size={14} /> : <Pause size={14} />}
                </button>
                <button
                    onClick={onReplay}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                    title="Replay"
                >
                    <RotateCcw size={14} />
                </button>
            </div>
        </div>
    );
}

export default function TalkToMarketSection() {
    const navigate = useNavigate();
    const [stage, setStage] = useState(0);
    const [typedText, setTypedText] = useState("");
    const [hasStarted, setHasStarted] = useState(false);
    const [query, setQuery] = useState("");
    const [isPaused, setIsPaused] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [stepProgress, setStepProgress] = useState(0);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Refs for controlling the animation
    const isPausedRef = useRef(false);
    const pausedAtRef = useRef<number>(0); // elapsed ms when paused
    const animStartRef = useRef<number>(0); // wall-clock start of current step
    const rafRef = useRef<number | undefined>(undefined);
    const timeoutsRef = useRef<number[]>([]);
    const typingTimerRef = useRef<number | undefined>(undefined);
    const startedRef = useRef(false); // guard against double-run

    const clearAll = useCallback(() => {
        timeoutsRef.current.forEach((id) => window.clearTimeout(id));
        timeoutsRef.current = [];
        if (typingTimerRef.current !== undefined) {
            window.clearInterval(typingTimerRef.current);
            typingTimerRef.current = undefined;
        }
        if (rafRef.current !== undefined) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = undefined;
        }
    }, []);

    // Progress bar RAF loop for a given step
    const runStepProgress = useCallback((step: number, startProgress: number = 0) => {
        const duration = STEP_DURATIONS[step] ?? 1000;
        const startTime = performance.now() - startProgress * duration;
        animStartRef.current = startTime;

        const tick = (now: number) => {
            if (isPausedRef.current) return;
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            setStepProgress(progress);
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(tick);
            }
        };
        if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);
    }, []);

    const startAnimation = useCallback((fromStep: number = 0) => {
        clearAll();
        isPausedRef.current = false;
        pausedAtRef.current = 0;
        setIsPaused(false);

        // Cumulative delays
        const delays = [0, 400, 1100, 1500, 2000];

        const runFrom = (step: number) => {
            // Set initial state for the starting step
            setCurrentStep(step);
            setStepProgress(0);
            runStepProgress(step);

            // stage transitions
            const stageOffset = delays[step] ?? 0;
            const baseNow = performance.now();

            const scheduleFrom = (absMs: number, fn: () => void) => {
                const relMs = Math.max(0, absMs - stageOffset);
                const id = window.setTimeout(() => {
                    if (isPausedRef.current) return;
                    fn();
                }, relMs);
                timeoutsRef.current.push(id);
            };

            if (step <= 0) {
                scheduleFrom(delays[0], () => {
                    setStage(0);
                    setTypedText('');
                });
            }
            if (step <= 0) {
                scheduleFrom(delays[1], () => {
                    setStage(1);
                    setCurrentStep(1);
                    setStepProgress(0);
                    runStepProgress(1);
                });
            }
            if (step <= 1) {
                scheduleFrom(delays[2], () => {
                    setStage(2);
                    setCurrentStep(2);
                    setStepProgress(0);
                    runStepProgress(2);
                });
            }
            if (step <= 2) {
                scheduleFrom(delays[3], () => {
                    setStage(3);
                    setCurrentStep(3);
                    setStepProgress(0);
                    runStepProgress(3);
                });
            }
            if (step <= 3) {
                scheduleFrom(delays[4], () => {
                    setStage(4);
                    setCurrentStep(3);
                    setStepProgress(1);
                });
                // Typing effect
                scheduleFrom(2200, () => {
                    let i = 0;
                    typingTimerRef.current = window.setInterval(() => {
                        if (isPausedRef.current) return;
                        i = Math.min(i + 2, FINAL_RESPONSE_TEXT.length);
                        setTypedText(FINAL_RESPONSE_TEXT.slice(0, i));
                        if (i >= FINAL_RESPONSE_TEXT.length && typingTimerRef.current !== undefined) {
                            window.clearInterval(typingTimerRef.current);
                            typingTimerRef.current = undefined;
                        }
                    }, 0.5);
                });
            }
            void baseNow; // suppress unused
        };

        // Jump immediately to the target step's visual state
        if (fromStep === 0) {
            setStage(0);
            setTypedText('');
        } else if (fromStep === 1) {
            setStage(1);
            setTypedText('');
        } else if (fromStep === 2) {
            setStage(2);
            setTypedText('');
        } else if (fromStep === 3) {
            setStage(3);
            setTypedText('');
        }

        runFrom(fromStep);
    }, [clearAll, runStepProgress]);

    // IntersectionObserver trigger
    useEffect(() => {
        if (hasStarted) return;
        const el = containerRef.current;
        if (!el) return;

        if (typeof IntersectionObserver === 'undefined') {
            setHasStarted(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry) return;
                if (entry.intersectionRatio >= 0.3) {
                    setHasStarted(true);
                    observer.disconnect();
                }
            },
            { threshold: [0, 0.3, 1] }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [hasStarted]);

    // Start animation when in view
    useEffect(() => {
        if (!hasStarted) return;
        if (startedRef.current) return;
        startedRef.current = true;
        startAnimation(0);
        return () => clearAll();
    }, [hasStarted, startAnimation, clearAll]);

    const handlePause = useCallback(() => {
        if (isPausedRef.current) {
            // Resume
            isPausedRef.current = false;
            setIsPaused(false);
            // Restart step progress from where we left off
            runStepProgress(currentStep, pausedAtRef.current);
        } else {
            // Pause
            isPausedRef.current = true;
            setIsPaused(true);
            pausedAtRef.current = stepProgress;
            if (rafRef.current !== undefined) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = undefined;
            }
        }
    }, [currentStep, stepProgress, runStepProgress]);

    const handleReplay = useCallback(() => {
        startedRef.current = true;
        startAnimation(0);
    }, [startAnimation]);

    const handleStepClick = useCallback((step: number) => {
        startedRef.current = true;
        startAnimation(step);
    }, [startAnimation]);

    return (
        <div
            ref={containerRef}
            className="relative bg-slate-50 text-slate-900 h-screen overflow-hidden font-sans selection:bg-blue-500/30 flex flex-col"
        >
            <header className="p-6 flex justify-between items-center z-50 bg-slate-50/80 backdrop-blur-sm border-b border-slate-200">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-1.5 rounded-lg">
                        <Sparkles size={18} className="text-white" />
                    </div>
                    <h1 className="font-semibold text-lg tracking-tight">FinSight <span className="text-slate-600 font-normal">AI</span></h1>
                    <h3 className='text-slate-400'>Conduct in-depth market analysis -- We'll handle this.</h3>
                </div>
            </header>

            <main className="flex-1 min-h-0 w-full px-4 md:px-6 pt-4 pb-6 relative flex flex-col gap-3" style={{overflow:"hidden"}}>
                <div className={`flex w-full h-[85vh] gap-6 transition-all duration-700 ease-in-out ${stage >= 3 ? 'translate-x-0' : ''}`}>
                    <VideoCarousel
                        title="Traditional Financial Information Analysis"
                        slides={TALK_SLIDES}
                        divider
                    />
                    <div className="flex flex-col justify-center transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] min-w-0" style={{ flex: '3.5 1 0' }}>
                        <div className={`text-center transition-opacity duration-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full ${stage > 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'
                            }`}>
                            <div
                                className={
                                    "mt-8 text-xs font-mono text-slate-400 " +
                                    (hasStarted ? "animate-pulse" : "")
                                }
                            >
                                Starting analysis...
                            </div>
                        </div>

                        <div className={`space-y-6 relative z-10 ${stage === 0 ? 'opacity-0 translate-y-10' : 'opacity-100 translate-y-0'} transition-all duration-700`}>
                            <div className={`flex items-start gap-4 justify-end transition-all duration-500 ${stage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                                }`}>
                                <div className="bg-blue-600 text-white p-4 rounded-2xl rounded-tr-sm shadow-lg max-w-[80%]">
                                    <p className="text-lg leading-relaxed">
                                        Analyze the latest market sentiment for the Consumer Cyclical sector.
                                    </p>
                                </div>
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-slate-200 shrink-0 shadow-sm">
                                    <User size={20} className="text-slate-500" />
                                </div>
                            </div>

                            {stage >= 2 && stage < 4 && (
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center border border-emerald-600/30 shrink-0">
                                        <Cpu size={20} className="text-emerald-500" />
                                    </div>
                                    <div className="bg-white border border-slate-200 p-4 rounded-2xl rounded-tl-sm text-slate-600 flex items-center gap-3 shadow-sm">
                                        <span className="relative flex h-3 w-3">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                                        </span>
                                        <span className="font-mono text-sm">Analyzing market data sources...</span>
                                    </div>
                                </div>
                            )}

                            {stage >= 3 && (
                                <div className={`flex items-start gap-4 transition-all duration-500 ${stage >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center border border-purple-200 shrink-0">
                                        <Search size={20} className="text-purple-500" />
                                    </div>
                                    <div className="bg-purple-50 border border-dashed border-purple-200 p-3 rounded-xl text-sm text-slate-600 flex flex-col gap-1 w-full max-w-[80%]">
                                        <div className="flex items-center gap-2 text-purple-600 font-medium">
                                            <Sparkles size={14} />
                                            <span>Tool: Market Data</span>
                                        </div>
                                        <p className="text-xs text-slate-500">Retrieving real-time market data...</p>
                                    </div>
                                </div>
                            )}

                            <div className={`flex items-start gap-4 transition-all duration-300 ${stage >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                                }`}>
                                <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-900/20 shrink-0">
                                    <Bot size={24} className="text-white" />
                                </div>
                                <div className="bg-white text-slate-900 p-5 rounded-2xl rounded-tl-sm shadow-xl border border-slate-100 w-full min-h-[200px]">
                                    <MarkdownRenderer content={typedText} theme="light" />
                                    {typedText.length < FINAL_RESPONSE_TEXT.length && (
                                        <span className="animate-pulse inline-block w-2 h-4 bg-emerald-500 ml-1 align-middle mt-2"></span>
                                    )}

                                </div>
                            </div>

                        </div>
                    </div>

                    <div className={`hidden md:flex flex-col justify-start transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] min-w-0 ${stage >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`} style={{ flex: '3.5 1 0' }}>
                        <div className="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl p-4 shadow-xl h-[74vh] overflow-y-auto w-full custom-scrollbar">
                            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                                {/* <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> */}
                                <h3 className="text-sm font-mono text-slate-500 uppercase tracking-widest">Live Feed From HiDy Dynamic Financial KG Engine</h3>
                            </div>

                            <div className={`mb-4 transition-all duration-500 delay-100 ${stage >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                                <h4 className="flex items-center gap-2 text-slate-700 font-semibold mb-2 text-sm">
                                    <Newspaper size={14} /> Latest Headlines
                                </h4>
                                <SectorNewsPanel panel={NEWS_PANEL_DATA} theme="light" />
                            </div>

                            <div className={`mb-4 transition-all duration-700 delay-300 ${stage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                                <h4 className="flex items-center gap-2 text-slate-700 font-semibold mb-2 text-sm">
                                    <BarChart3 size={14} /> Market Trend
                                </h4>
                                <MarketIndexCard
                                    mockData={chartData}
                                    theme="light"
                                    readOnly
                                    className="bg-slate-50 border-slate-200"
                                    initialRange="5D"
                                    initialSymbols={[".INX:INDEXSP", "NVDA:NASDAQ", "TSLA:NASDAQ"]}
                                />
                            </div>

                            <div className={`transition-all duration-700 delay-500 ${stage >= 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                                <h4 className="flex items-center gap-2 text-slate-700 font-semibold mb-2 text-sm">
                                    <Activity size={14} /> Sector Heatmap
                                </h4>
                                <div>
                                    <MarketHeatmapCard
                                        autoFetch={false}
                                        collections={MOCK_COLLECTIONS}
                                        initialCollectionId="nasdaq"
                                        heatmap={HEATMAP_DATA}
                                        className="h-full bg-slate-50 border-slate-200"
                                        theme="light"
                                    />
                                </div>
                            </div>

                        </div>
                    </div>

                </div>

                {/* Progress bar + controls — fixed bottom-right */}
                <div className="absolute bottom-6 right-6 z-50">
                    <StepProgressBar
                        currentStep={currentStep}
                        stepProgress={stepProgress}
                        isPaused={isPaused}
                        onStepClick={handleStepClick}
                        onPause={handlePause}
                        onReplay={handleReplay}
                    />
                </div>

                <div className="mt-4 w-full max-w-4xl mx-auto shrink-0">
                    <ChatBar
                        Icon={ArrowUpRight}
                        value={query}
                        onChange={setQuery}
                        placeholder="Ask K-Quant to analyze market data..."
                        onSend={(text) => {
                            const t = (text ?? "").trim();
                            if (!t) return;
                            setQuery("");
                            navigate("/smart-insights/chat", { state: { prompt: t } });
                        }}
                    />
                </div>
            </main>
        </div>
    );
}
